import { useMemo } from 'react';
import { CreditCardInput } from './CreditCardInput';
import { useInvitationContext } from '../context/InvitationContext';
import { Notice } from '../../../components/ui/Notice';
import { Button } from '../../../components/ui/Button';
import { StepHeader } from '../../../components/ui/StepHeader';
import { useT } from '../../../i18n/admin';

// SAM-199: ported off baseui/styletron — this file no longer imports
// `baseui`. Same fields, same validation, same 3DS hookup in
// AddInvitation.tsx; only the presentation moved to Tailwind + the DS
// tokens/components (Notice, Button, StepHeader).

interface PaymentStepProps {
  onNext: () => void;
  onBack: () => void;
  isLoading?: boolean;
  error?: string | null;
  onClearError?: () => void;
  // SAM-199: true for a SERVICE booking, where the server (not this form) decides whether the
  // selection is actually free — never trust the client's price. A card is optional here: the
  // operator can continue with none entered (free path) or fill one in fully (paid path, driven
  // through 3DS in AddInvitation.tsx). A PARTIALLY filled card still blocks Continue either way —
  // this only changes whether an EMPTY card is allowed through. The product path (cardOptional
  // omitted/false) is completely unaffected: it always requires a fully valid card, unchanged.
  cardOptional?: boolean;
}

export const PaymentStep = ({ onNext, onBack, isLoading = false, error, onClearError, cardOptional = false }: PaymentStepProps) => {
  const { invitation, updateInvitation } = useInvitationContext();
  const { t, fmt, lang } = useT();
  const cardLabels = useMemo(() => ({
    cardNumber: t('card.number'), cardHolder: t('card.holder'), expiryDate: t('card.expiry'), cvv: t('card.cvv'),
    required: t('card.required'), invalidCardNumber: t('card.invalidNumber'), invalidExpiry: t('card.invalidExpiry'),
    expired: t('card.expired'), invalidCvv: t('card.invalidCvv'), invalidName: t('card.invalidName'),
  }), [t]);

  // Get product details
  const productDetails = useMemo(() => {
    if (!invitation?.product?.[0]) return null;
    return {
      name: invitation.product[0].label?.split(' - ')[0] || '',
      price: invitation.product[0].price || 0
    };
  }, [invitation]);

  // SAM-199 — service booking summary (no price: the server recomputes the total on submit).
  const serviceSummary = useMemo(() => {
    const sb = invitation?.serviceBooking;
    if (!sb) return null;
    const guestCount = sb.quantities.reduce((sum, q) => sum + q.quantity, 0);
    return { name: sb.serviceName, date: sb.date, time: sb.time, guestCount };
  }, [invitation?.serviceBooking]);

  // Check if credit card is filled
  const isCreditCardValid = useMemo(() => {
    const cc = invitation?.creditCard;
    if (!cc) return false;

    // Basic validation - all fields must be filled
    return cc.cardNumber?.length === 16 &&
           cc.cardHolder?.length >= 3 &&
           cc.expiryDate?.match(/^\d{2}\/\d{2}$/) &&
           cc.cvv?.length >= 3;
  }, [invitation?.creditCard]);

  const isCreditCardEmpty = useMemo(() => {
    const cc = invitation?.creditCard;
    return !cc || (!cc.cardNumber && !cc.cardHolder && !cc.expiryDate && !cc.cvv);
  }, [invitation?.creditCard]);

  const canContinue = cardOptional ? (isCreditCardValid || isCreditCardEmpty) : isCreditCardValid;

  return (
    <div className="w-full mx-auto p-6" style={{ maxWidth: 450 }}>
      <StepHeader title={t('booking.payment.title')} description={t('booking.payment.description')} />

      {productDetails && (
        <div className="mb-6 p-4" style={{ background: 'var(--surface)', borderRadius: 'var(--r-12)' }}>
          <div className="text-base font-medium mb-1" style={{ color: 'var(--ink-900)' }}>{productDetails.name}</div>
          <div className="text-2xl font-semibold" style={{ color: 'var(--ink-900)' }}>${productDetails.price.toFixed(2)}</div>
        </div>
      )}

      {serviceSummary && (
        <div className="mb-6 p-4" style={{ background: 'var(--surface)', borderRadius: 'var(--r-12)' }}>
          <div className="text-base font-medium mb-1" style={{ color: 'var(--ink-900)' }}>{serviceSummary.name}</div>
          <div className="text-[13px]" style={{ color: 'var(--ink-500)' }}>
            {t('booking.payment.when', { date: fmt.date(serviceSummary.date), time: serviceSummary.time })} · {t(serviceSummary.guestCount === 1 ? 'booking.guests.one' : 'booking.guests.many', { n: serviceSummary.guestCount })}
          </div>
          <div className="text-xs mt-2" style={{ color: 'var(--ink-400)' }}>
            {t('booking.payment.cardOptional')}
          </div>
        </div>
      )}

      {error && (
        <Notice variant="error" className="mb-6 mt-4">
          {error}
        </Notice>
      )}

      <CreditCardInput
        value={invitation?.creditCard}
        onChange={(value) => {
          updateInvitation({ creditCard: value });
          // COMMENTED: Don't clear error automatically - let user see it
          // if (error && onClearError) {
          //   onClearError();
          // }
        }}
        disabled={isLoading}
        // English keeps the form's own, more specific messages; Spanish gets the translated set.
        labels={lang === 'en' ? undefined : cardLabels}
      />

      <div className="mt-6 flex gap-3">
        <Button variant="ghost" className="w-1/2" onClick={onBack} disabled={isLoading}>
          {t('common.back')}
        </Button>
        <Button variant="primary" className="w-1/2" onClick={onNext} disabled={!canContinue || isLoading} isLoading={isLoading}>
          {t('common.continue')}
        </Button>
      </div>
    </div>
  );
};
